/* ================================================================
   THEATER PAGE (Checkpoint 34, extended 43/44, rebuilt CP-THEATER,
   CP70.7 — client-side-only rebuild)
   ----------------------------------------------------------------
   Private screening room, now fully client-side and nothing else:
   a visitor picks a video file off their OWN device, it plays right
   here, wrapped in an LFJ-branded "package" — a randomized intro
   bumper (1 of 5), then the video, then a randomized outro (1 of 5,
   picked independently of the intro — 25 possible pairings a
   session). Nothing is uploaded, nothing is saved, nothing touches
   Shopify or any server. Gone on reload, same as any other in-memory
   JS state.

   There is no catalog, no reel, no admin step of any kind — CP70.7
   removed the old theater-reel.json / "upload in Admin -> Files"
   system entirely, per direct instruction: that concept had nothing
   to do with what this room is for. The only way a video ever plays
   here is a visitor's own <input type="file"> selection.

   FLOW, in order:
     1. Idle state — big central prompt + a small persistent corner
        button (either one opens the file picker; same handler).
     2. File chosen -> START GATE: an LFJ-branded "PRESS START" card.
        Auto-advances after ~2.5s if untouched; a click/tap/Enter/
        Space advances immediately. Exists so there's a beat to get
        ready before anything happens — not an accident, a request.
     3. LIGHTS DOWN — a real full-viewport fade to solid black over
        ~2.2s (a dedicated black overlay above everything, not reused
        mode-dependent theme color, so it's an actual black regardless
        of which color mode is active). The "walking into a theater as
        the lights dim" beat.
     4. Once fully black: one of 5 intro variants plays (randomized),
        an outro variant is ALSO picked right now, independently, for
        later — then a short loading bar, then the video itself.
     5. On end: the independently-chosen outro plays, then a replay
        button (restarts the same in-memory file, doesn't reload —
        reloading would lose the File object with no way to recover
        it, since nothing was ever uploaded anywhere).

   5 intro / 5 outro variants — structural homage to real mainline
   Pokemon game intros (Gen 1/4/5 opening beats — shooting star +
   falling sparkles, silhouette-then-color-burst reveal, skyline zoom
   into an emblem, a power-on bloom, a curtain-and-marquee theater
   entrance), rebuilt with 100% original content: an original
   aperture/shutter silhouette (not a creature), the site's own
   multicolor LFJ wordmark, an original palette, no trademarks, no
   copied assets. See lfj-theater.css header for the full research
   notes (unchanged from Checkpoint 44) and CP70.7's extended timing.

   Custom pixel control bar: play/pause, skip +-10s, seek, volume,
   mute, speed, picture-in-picture, fullscreen. Original synthesized
   SFX (Web Audio, same approach as the site's existing click/nav
   sounds — no third-party audio, nothing licensed).

   Mirrors the safeRun/onReady conventions used across
   lfj-page-transition.js and lfj-intro.js so a failure here can't
   take down the rest of the page.
================================================================ */
(function () {
  'use strict';

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  function safeRun(label, fn) {
    try { fn(); } catch (e) {
      try { console.error('[lfj-theater]', label, e); } catch (_e) {}
    }
  }

  var VARIANTS = ['shooting-star', 'silhouette-charge', 'skyline-zoom', 'power-on', 'theater-entrance'];
  var reducedMotion = false;
  try {
    reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {}

  function pickVariant() {
    return VARIANTS[Math.floor(Math.random() * VARIANTS.length)];
  }

  /* CP70.7 — each variant now runs a shared "charge" pre-beat (a
     brief held-black moment with a single pulse of light) before its
     own already-distinct payoff, then holds longer on the resolved
     mark before the card fades in. Roughly doubles each variant's
     original ~2s to a real ~4.4s build, closer to an actual game's
     opening bumper without hand-authoring five entirely separate
     multi-stage sequences from scratch — the payoffs themselves
     (the actual visual identity of each variant) are untouched from
     Checkpoint 44's research-backed builds. */
  var INTRO_MS = reducedMotion ? 260 : 4400;
  var OUTRO_MS = reducedMotion ? 220 : 2000;
  var GATE_AUTO_MS = 2600;
  var DIM_MS = reducedMotion ? 200 : 2200;

  /* ---- shared SFX (same __lfjSharedAudioCtx convention as
     lf-click-sfx.js / lf-fm-player.js — one AudioContext per page,
     never a second one spun up here) ---- */
  function getAudioCtx() {
    try {
      var AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      if (!window.__lfjSharedAudioCtx) window.__lfjSharedAudioCtx = new AC();
      var ctx = window.__lfjSharedAudioCtx;
      if (ctx.state === 'suspended') ctx.resume().catch(function () {});
      return ctx;
    } catch (e) { return null; }
  }
  function playTone(freqStart, freqEnd, durMs, type) {
    if (reducedMotion) return;
    try {
      var ctx = getAudioCtx();
      if (!ctx) return;
      var osc = ctx.createOscillator();
      var gain = ctx.createGain();
      osc.type = type || 'square';
      var now = ctx.currentTime;
      var dur = durMs / 1000;
      osc.frequency.setValueAtTime(freqStart, now);
      osc.frequency.linearRampToValueAtTime(freqEnd, now + dur);
      gain.gain.setValueAtTime(0.001, now);
      gain.gain.linearRampToValueAtTime(0.14, now + Math.min(0.02, dur / 4));
      gain.gain.linearRampToValueAtTime(0.0001, now + dur);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(now); osc.stop(now + dur + 0.02);
    } catch (e) {}
  }
  var SFX = {
    gateBlip:   function () { playTone(440, 440, 90, 'square'); },
    dimHum:     function () { playTone(80, 40, 1800, 'sine'); },
    chargeTick: function () { playTone(140, 140, 60, 'square'); },
    introBoot:  function () { playTone(220, 880, 260, 'square'); },
    introChime: function () { playTone(660, 1320, 180, 'triangle'); },
    outroDown:  function () { playTone(880, 110, 520, 'sawtooth'); },
    click:      function () { playTone(1320, 1320, 45, 'square'); }
  };
  var VARIANT_INTRO_SFX = {
    'shooting-star': SFX.introChime,
    'silhouette-charge': SFX.introBoot,
    'skyline-zoom': SFX.introChime,
    'power-on': SFX.introBoot,
    'theater-entrance': SFX.introChime
  };

  onReady(function () {
    safeRun('theater init', function () {
      var stage = document.getElementById('lfjTheaterStage');
      if (!stage) return;

      var startgateEl = document.getElementById('lfjTheaterStartgate');
      var dimEl     = document.getElementById('lfjTheaterDim');
      var introEl   = document.getElementById('lfjTheaterIntro');
      var outroEl   = document.getElementById('lfjTheaterOutro');
      var loadingEl = document.getElementById('lfjTheaterLoading');
      var playerEl  = document.getElementById('lfjTheaterPlayer');
      var emptyEl   = document.getElementById('lfjTheaterEmpty');
      var titleEl   = document.getElementById('lfjTheaterTitle');
      var replayBtn = document.getElementById('lfjTheaterReplay');
      var cardTitle = document.getElementById('lfjTheaterCardTitle');
      var controlbar = document.getElementById('lfjTheaterControlbar');
      var playPauseBtn = document.getElementById('lfjTheaterPlayPause');
      var muteBtn = document.getElementById('lfjTheaterMute');
      var fsBtn = document.getElementById('lfjTheaterFullscreen');
      var seekEl = document.getElementById('lfjTheaterSeek');
      var seekFill = document.getElementById('lfjTheaterSeekFill');
      var timeEl = document.getElementById('lfjTheaterTime');
      var skipBackBtn = document.getElementById('lfjTheaterSkipBack');
      var skipFwdBtn = document.getElementById('lfjTheaterSkipFwd');
      var volSlider = document.getElementById('lfjTheaterVolume');
      var speedBtn = document.getElementById('lfjTheaterSpeed');
      var pipBtn = document.getElementById('lfjTheaterPiP');

      var controller = null;      /* set once a video is live */
      var localObjectURL = null;  /* current blob: URL, revoked before replacing */
      var pendingOutroVariant = 'shooting-star';

      function fmtTime(sec) {
        sec = Math.max(0, Math.floor(sec || 0));
        var m = Math.floor(sec / 60), s = sec % 60;
        return m + ':' + (s < 10 ? '0' : '') + s;
      }

      /* ---- 1. START GATE ------------------------------------- */
      function runStartGate(done) {
        if (!startgateEl) { done(); return; }
        startgateEl.hidden = false;
        SFX.gateBlip();
        var advanced = false;
        var timer = null;
        function advance() {
          if (advanced) return;
          advanced = true;
          if (timer) clearTimeout(timer);
          startgateEl.removeEventListener('click', advance);
          document.removeEventListener('keydown', onKey);
          startgateEl.hidden = true;
          done();
        }
        function onKey(e) {
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') advance();
        }
        startgateEl.addEventListener('click', advance);
        document.addEventListener('keydown', onKey);
        timer = setTimeout(advance, reducedMotion ? 0 : GATE_AUTO_MS);
      }

      /* ---- 2. LIGHTS DOWN — real black fade over the whole stage,
         not a reused theme color, so it reads as genuinely going
         dark in every color mode. ---------------------------------- */
      function runDim(done) {
        if (!dimEl) { done(); return; }
        SFX.dimHum();
        dimEl.classList.add('is-dimming');
        setTimeout(function () {
          done();
        }, DIM_MS);
      }

      function runUndim() {
        if (!dimEl) return;
        /* Instant, not the slow fade-in transition — this fires at
           the exact moment we hand off to the intro, which already
           builds up FROM black on its own (each variant's own
           design); a slow fade-out here would visibly fight that. */
        dimEl.style.transition = 'none';
        dimEl.classList.remove('is-dimming');
        dimEl.offsetHeight; /* force reflow before re-enabling the transition */
        dimEl.style.transition = '';
      }

      function showEmpty() {
        if (introEl) introEl.style.display = 'none';
        if (loadingEl) loadingEl.style.display = 'none';
        if (emptyEl) emptyEl.hidden = false;
      }
      function hideEmpty() {
        if (emptyEl) emptyEl.hidden = true;
      }

      /* ---- 3. INTRO / OUTRO ----------------------------------- */
      function runIntro(fileName, variant, done) {
        if (!introEl) { done(); return; }
        introEl.style.display = '';
        introEl.classList.remove('is-leaving');
        introEl.setAttribute('data-tv-variant', variant);
        if (cardTitle) cardTitle.textContent = fileName || window.LF_THEATER_TITLE || 'LFJ';

        var sfx = VARIANT_INTRO_SFX[variant];
        if (sfx) setTimeout(sfx, reducedMotion ? 0 : 900); /* fires at the payoff beat, after the charge pre-beat */

        setTimeout(function () {
          introEl.classList.add('is-leaving');
          setTimeout(function () {
            introEl.style.display = 'none';
            done();
          }, reducedMotion ? 0 : 320);
        }, INTRO_MS);
      }

      function runOutro(variant, done) {
        if (!outroEl) { if (done) done(); return; }
        outroEl.setAttribute('data-tv-variant', variant);
        SFX.outroDown();
        outroEl.classList.add('is-active');
        setTimeout(function () {
          outroEl.classList.remove('is-active');
          if (done) done();
        }, OUTRO_MS);
      }

      function runLoading(done) {
        if (!loadingEl) { done(); return; }
        loadingEl.style.display = '';
        loadingEl.classList.add('is-visible');
        var fill = document.getElementById('lfjTheaterLoadingFill');
        if (fill) {
          fill.style.animation = 'none';
          fill.offsetHeight; /* force reflow — same fix as the site-wide loader */
          fill.style.animation = '';
        }
        var loadMs = reducedMotion ? 300 : 1200;
        setTimeout(function () {
          loadingEl.style.display = 'none';
          done();
        }, loadMs);
      }

      /* CP70.9 — 3-2-1 countdown, between loading finishing and the video
         actually starting. Ticks respect reducedMotion the same way every
         other step in this file already does. */
      var COUNT_TICK_MS = reducedMotion ? 60 : 700;
      function runCountdown(done) {
        var el = document.getElementById('lfjTheaterCountdown');
        var numEl = document.getElementById('lfjTheaterCountdownNum');
        if (!el || !numEl) { done(); return; }
        el.style.display = 'flex';
        var n = 3;
        function tick() {
          numEl.textContent = n;
          numEl.classList.remove('is-punch');
          void numEl.offsetWidth; /* restart the punch animation each tick */
          numEl.classList.add('is-punch');
          playTone(523, 523, 120, 'square');
          n--;
          if (n > 0) {
            setTimeout(tick, COUNT_TICK_MS);
          } else {
            setTimeout(function () { el.style.display = 'none'; done(); }, COUNT_TICK_MS);
          }
        }
        tick();
      }

      /* ---- Controller interface: play/pause/skip/seek/volume/rate/
         PiP/fullscreen for the native <video> element. ---- */
      function bindVideoController(video) {
        return {
          play: function () { video.play().catch(function () {}); },
          pause: function () { video.pause(); },
          isPaused: function () { return video.paused; },
          seekFraction: function (f) { if (video.duration) video.currentTime = f * video.duration; },
          skip: function (secs) { video.currentTime = Math.max(0, Math.min(video.duration || 0, video.currentTime + secs)); },
          getFraction: function () { return video.duration ? video.currentTime / video.duration : 0; },
          getTimeLabel: function () { return fmtTime(video.currentTime) + ' / ' + fmtTime(video.duration); },
          toggleMute: function () { video.muted = !video.muted; return video.muted; },
          setVolume: function (v) { video.volume = Math.max(0, Math.min(1, v)); if (v > 0) video.muted = false; },
          setRate: function (r) { video.playbackRate = r; },
          requestPiP: function () {
            if (document.pictureInPictureEnabled && video.requestPictureInPicture) {
              video.requestPictureInPicture().catch(function () {});
            }
          },
          supportsPiP: !!(document.pictureInPictureEnabled && video.requestPictureInPicture),
          onTick: function (cb) { video.addEventListener('timeupdate', cb); },
          onEnded: function (cb) { video.addEventListener('ended', cb); },
          onReady: function (cb) { cb(); },
          fullscreenTarget: function () { return video; }
        };
      }

      /* CP67 — control-bar glyphs are pixel SVGs; JS only flips a
         state attribute, CSS decides which icon shows. */
      function setPlayState(state) {
        if (!playPauseBtn) return;
        playPauseBtn.setAttribute('data-state', state);
        playPauseBtn.setAttribute('aria-label', state === 'pause' ? 'Pause' : 'Play');
      }
      function setMutedState(muted) {
        if (!muteBtn) return;
        muteBtn.classList.toggle('is-muted', !!muted);
        muteBtn.setAttribute('aria-label', muted ? 'Unmute' : 'Mute');
      }

      function wireControlBar(ctrl, onEnded) {
        controller = ctrl;
        if (!controlbar) return;
        controlbar.hidden = false;
        ctrl.onReady(function () {
          setPlayState('pause');
        });
        if (playPauseBtn) {
          playPauseBtn.onclick = function () {
            if (ctrl.isPaused()) { ctrl.play(); setPlayState('pause'); }
            else { ctrl.pause(); setPlayState('play'); }
            SFX.click();
          };
        }
        if (skipBackBtn) {
          skipBackBtn.onclick = function () { ctrl.skip(-10); SFX.click(); };
        }
        if (skipFwdBtn) {
          skipFwdBtn.onclick = function () { ctrl.skip(10); SFX.click(); };
        }
        if (muteBtn) {
          muteBtn.onclick = function () {
            var muted = ctrl.toggleMute();
            setMutedState(muted);
            if (volSlider) volSlider.value = muted ? 0 : 1;
            SFX.click();
          };
        }
        if (volSlider) {
          volSlider.oninput = function () {
            var v = parseFloat(volSlider.value);
            ctrl.setVolume(v);
            setMutedState(v === 0);
          };
        }
        if (speedBtn) {
          var rates = [1, 1.25, 1.5, 2, 0.5, 0.75];
          var rateIdx = 0;
          speedBtn.onclick = function () {
            rateIdx = (rateIdx + 1) % rates.length;
            var r = rates[rateIdx];
            ctrl.setRate(r);
            speedBtn.textContent = r + 'x';
            SFX.click();
          };
        }
        if (pipBtn) {
          pipBtn.hidden = !ctrl.supportsPiP;
          pipBtn.onclick = function () { ctrl.requestPiP(); SFX.click(); };
        }
        if (fsBtn) {
          fsBtn.onclick = function () {
            SFX.click();
            try {
              var t = ctrl.fullscreenTarget();
              if (t.requestFullscreen) t.requestFullscreen();
              else if (t.webkitRequestFullscreen) t.webkitRequestFullscreen();
            } catch (e) {}
          };
        }
        if (seekEl) {
          seekEl.onclick = function (e) {
            var rect = seekEl.getBoundingClientRect();
            var frac = (e.clientX - rect.left) / rect.width;
            ctrl.seekFraction(Math.max(0, Math.min(1, frac)));
          };
        }
        ctrl.onTick(function () {
          if (seekFill) seekFill.style.width = (ctrl.getFraction() * 100) + '%';
          if (timeEl) timeEl.textContent = ctrl.getTimeLabel();
        });
        ctrl.onEnded(function () {
          setPlayState('play');
          onEnded();
        });
      }

      function onVideoEnded() {
        if (controlbar) controlbar.hidden = true;
        if (playerEl) playerEl.classList.remove('is-active');
        runOutro(pendingOutroVariant, function () {
          if (replayBtn) replayBtn.hidden = false;
        });
      }

      function playSource(title, src) {
        if (!playerEl) return;
        if (titleEl) titleEl.textContent = title || '';

        var mount = document.getElementById('lfjTheaterMount');
        if (!mount) return;
        mount.innerHTML = '';

        var video = document.createElement('video');
        video.src = src;
        video.controls = false; /* custom bar replaces native controls */
        video.autoplay = true;
        video.playsInline = true;
        mount.appendChild(video);
        var vidCtrl = bindVideoController(video);
        wireControlBar(vidCtrl, onVideoEnded);

        playerEl.classList.add('is-active');
        if (replayBtn) {
          replayBtn.hidden = true;
          replayBtn.onclick = function () {
            /* Restart in place rather than reload — a reload would
               lose the File the browser handed us; there is no url
               or server-side reference to recover it from, since
               nothing was ever uploaded anywhere. */
            var m = document.getElementById('lfjTheaterMount');
            var v = m && m.querySelector('video');
            if (!v) return;
            replayBtn.hidden = true;
            playerEl.classList.add('is-active');
            if (controlbar) controlbar.hidden = false;
            v.currentTime = 0;
            v.play().catch(function () {});
            setPlayState('pause'); /* the onEnded handler above leaves this on 'play' */
          };
        }
      }

      /* CP70.7 — the entire room's only entry point. Fully client-
         side: URL.createObjectURL() makes a temporary local blob:
         reference to the File the browser already has in memory from
         the <input type="file"> picker. Nothing is read over the
         network, nothing is sent anywhere, nothing persists past
         this page session. The previous object URL is explicitly
         revoked before creating a new one so picking several files
         in one session doesn't leak memory. */
      function playLocalFile(file) {
        if (!file || !/^video\//.test(file.type)) return;

        /* Swapping files while one is already playing shouldn't leave
           the OLD video's audio running underneath the new gate/dim/
           intro sequence for several seconds — stop it immediately. */
        var existingMount = document.getElementById('lfjTheaterMount');
        var existingVideo = existingMount && existingMount.querySelector('video');
        if (existingVideo) existingVideo.pause();

        if (localObjectURL) URL.revokeObjectURL(localObjectURL);
        localObjectURL = URL.createObjectURL(file);

        hideEmpty();
        if (controlbar) controlbar.hidden = true;
        if (replayBtn) replayBtn.hidden = true;
        if (playerEl) playerEl.classList.remove('is-active');

        runStartGate(function () {
          runDim(function () {
            /* CP70.11 — runUndim() now fires here, the moment the dim
               phase hands off to the show, matching what
               .lfj-theater-dim's own CSS comment always said it should
               do ("instantly cleared the moment the intro takes over").
               It was previously called inside runIntro()'s COMPLETION
               callback instead of at its start — meaning the entire
               intro was playing hidden behind the still-opaque dim
               overlay (dim is z-index 15, intro is z-index 4) the whole
               time, only revealing anything right as intro was already
               ending. Moving it here also makes the countdown below
               actually visible, which needed the same fix regardless. */
            runUndim();
            var introVariant = pickVariant();
            pendingOutroVariant = pickVariant(); /* independent of the intro — picked now, used later, on end */
            /* CP70.7-fix — display title only, extension stripped
               ("clip.mov" -> "clip"); this was in CP70.6 and silently
               dropped in this rebuild's fresh playSource()/runIntro()
               calls. Doesn't touch localObjectURL or the actual file. */
            var displayTitle = file.name ? file.name.replace(/\.[^./\\]+$/, '') : file.name;
            /* CP70.11 — "count first, then start the intro": countdown
               moved from between loading/playSource to right here,
               before runIntro() is even called. */
            runCountdown(function () {
              runIntro(displayTitle, introVariant, function () {
                runLoading(function () {
                  playSource(displayTitle, localObjectURL);
                });
              });
            });
          });
        });
      }

      function wireLocalFileButton() {
        var btn = document.getElementById('lfjTheaterLocalBtn');
        var input = document.getElementById('lfjTheaterLocalInput');
        if (!btn || !input) return;
        function open() { input.click(); }
        btn.addEventListener('click', open);
        if (emptyEl) {
          emptyEl.addEventListener('click', open);
          emptyEl.addEventListener('keydown', function (e) {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); }
          });
        }
        input.addEventListener('change', function () {
          var file = input.files && input.files[0];
          if (file) playLocalFile(file);
          input.value = ''; /* allows re-picking the same file later */
        });
      }
      wireLocalFileButton();

      /* Nothing auto-plays. The room starts idle, showing the empty
         state, until a visitor picks a file — that's the whole point. */
      showEmpty();
    });
  });
})();
